import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Save, Trash2, Bookmark } from 'lucide-react';
import { api } from '@/api/api-client';
import { toast } from 'sonner';

interface SavedView {
  id?: string;
  name: string;
  filters?: Record<string, unknown>;
}

interface SavedViewsApi {
  filter(query: Record<string, unknown>, sort: string, limit: number): Promise<SavedView[]>;
  update(id: string, patch: Partial<SavedView>): Promise<unknown>;
  create(data: Partial<SavedView> & { scope?: string }): Promise<unknown>;
  delete(id: string): Promise<unknown>;
}

interface ApiShape {
  entities: { SavedView: SavedViewsApi };
}

interface SavedViewsProps {
  clientEmail?: string;
  filters: Record<string, unknown>;
  onApply?: (filters: Record<string, unknown>) => void;
  scope?: string;
}

export default function SavedViews({ clientEmail, filters, onApply, scope = 'client-dashboard' }: SavedViewsProps) {
  const { t } = useTranslation(['admin']);
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState('');

  const { data: views = [] } = useQuery<SavedView[]>({
    queryKey: ['saved-views', clientEmail, scope],
    queryFn: () => (api as unknown as ApiShape).entities.SavedView.filter({ scope }, '-created_date', 100),
    enabled: !!clientEmail,
  });

  const saveCurrent = async () => {
    const name = window.prompt(t('admin:backoffice.savedViews.namePrompt'), t('admin:backoffice.savedViews.defaultName'));
    if (!name) return;
    try {
      const existing = views.find((v) => v.name === name);
      if (existing?.id) {
        await (api as unknown as ApiShape).entities.SavedView.update(existing.id, { filters });
      } else {
        await (api as unknown as ApiShape).entities.SavedView.create({ scope, name, filters });
      }
      queryClient.invalidateQueries({ queryKey: ['saved-views', clientEmail, scope] });
      setCurrent(name);
      toast.success(t('admin:backoffice.savedViews.saved'));
    } catch {
      toast.error(t('admin:backoffice.savedViews.saveError'));
    }
  };

  const applySelected = (name: string) => {
    setCurrent(name);
    const v = views.find((x) => x.name === name);
    if (v && onApply) onApply(v.filters || {});
  };

  const removeSelected = async () => {
    if (!current) return;
    const v = views.find((x) => x.name === current);
    if (!v?.id) return;
    try {
      await (api as unknown as ApiShape).entities.SavedView.delete(v.id);
      queryClient.invalidateQueries({ queryKey: ['saved-views', clientEmail, scope] });
      setCurrent('');
      toast.success(t('admin:backoffice.savedViews.deleted'));
    } catch {
      toast.error(t('admin:backoffice.savedViews.deleteError'));
    }
  };

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2 ml-auto">
        <Bookmark className="w-4 h-4 text-gray-400" />
        <Select value={current} onValueChange={applySelected}>
          <SelectTrigger className="h-9 w-44">
            <SelectValue placeholder={t('admin:backoffice.savedViews.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {views.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-500">{t('admin:backoffice.savedViews.noView')}</div>
            ) : views.map((v) => (
              <SelectItem key={v.id || v.name} value={v.name}>{v.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="outline" onClick={saveCurrent} className="h-9 px-2">
              <Save className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('admin:backoffice.savedViews.saveCurrent')}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="outline" onClick={removeSelected} disabled={!current} className="h-9 px-2">
              <Trash2 className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('admin:backoffice.savedViews.deleteSelected')}</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
